import type { Column, Table } from '@tanstack/react-table';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { DataTableHeaderCell } from '../../_base/index.js';
import { DataTableHeaderCellList } from '../../_base/list/index.js';
import { DataTableHeaderCellSurface } from '../../_base/surface/index.js';
import { useListFilter } from '../../_hooks/list/_shared/hooks/list-filter.js';
import { useNumberFilterData } from '../../_hooks/list/number.js';
import { useOpenPopover } from '../../_hooks/open-popover.js';
import { ResetFilterMenuItem } from '../../_shared/reset.js';
import { typedSetNumberColumnFilterValue } from '../filter/index.js';
import { NUMBER_FILTER_OPERATION, type NumberFilterValuesWithoutInArray } from '../filter/operation.js';
import type { NumberFilterSchemaWithoutInArray } from '../filter/schema.js';
import { DROPDOWN_NUMBER_OPTIONS } from './data.js';
import { NumberFilter } from './filter.js';
import { NumberOperation } from './operation.js';

type DataTableHeaderCellNumberProps<TData, TValue> = {
    tableId: string;
    table: Table<TData>;
    column: Column<TData, TValue>;
    title: ReactNode;
    tooltip: ReactNode;
};

export function DataTableHeaderCellNumber<TData, TValue>({
    tableId,
    table,
    column,
    title,
    tooltip,
}: DataTableHeaderCellNumberProps<TData, TValue>) {
    const filter = column.getFilterValue() as NumberFilterSchemaWithoutInArray | undefined;

    const [dropdownValue, setDropdownValue] = useState<NumberFilterValuesWithoutInArray['id']>(
        filter?.operation ?? DROPDOWN_NUMBER_OPTIONS[0][0]
    );

    const { open, openPopover, closePopover, onOpenChange } = useOpenPopover();

    const disabled = useMemo(() => table.options.data.length > 10_000, [table.options.data]);
    const data = useNumberFilterData({ disabled, table, column });

    const onResetDropdownValue = useCallback(() => {
        setDropdownValue(DROPDOWN_NUMBER_OPTIONS[0][0]);
    }, []);

    const onRegularSelectionChange = useCallback((selectedValues: (string | number)[], items: string[]) => {
        setDropdownValue(NUMBER_FILTER_OPERATION.inArray.id as NumberFilterValuesWithoutInArray['id']);

        typedSetNumberColumnFilterValue(column, {
            operation: NUMBER_FILTER_OPERATION.inArray.id,
            array: selectedValues.filter((value) => typeof value === 'number'),
            includeBlanks: items.includes('select-blanks'),
        });
    }, []);

    const { selectedItems, handleSelectionChange, onApplyFilter, resetListFilter } = useListFilter({
        data,
        disabled: disabled,
        tableId,
        column,
        onResetDropdownValue,
        onRegularSelectionChange,
    });

    return (
        <DataTableHeaderCell
            column={column}
            title={title}
            open={open}
            openPopover={openPopover}
            closePopover={closePopover}
            onOpenChange={onOpenChange}
            tooltip={tooltip}
            popover={
                <DataTableHeaderCellSurface
                    column={column}
                    closePopover={closePopover}
                    filterNode={
                        <>
                            <ResetFilterMenuItem
                                column={column}
                                closePopover={closePopover}
                                resetOperation={resetListFilter}
                            />

                            <NumberOperation
                                column={column}
                                dropdownValue={dropdownValue}
                                resetListFilter={resetListFilter}
                                setDropdownValue={setDropdownValue}
                            />

                            {dropdownValue ===
                            (NUMBER_FILTER_OPERATION.inArray.id as NumberFilterValuesWithoutInArray['id']) ? null : (
                                <NumberFilter column={column} dropdownValue={dropdownValue} />
                            )}

                            {!disabled && (
                                <DataTableHeaderCellList
                                    data={data}
                                    selectedItems={selectedItems}
                                    handleSelectionChange={handleSelectionChange}
                                    onApplyFilter={onApplyFilter}
                                />
                            )}
                        </>
                    }
                />
            }
        />
    );
}
